import { useSelector } from "react-redux";
import { type RootState } from "~/app/store";
import { selectProductResultAmount } from "~/features/search/redux/searchSelectors";
import { selectNavigationBreadcrumbs } from "~/features/breadcrumbs/redux/selectors";
import { ImageFormat } from "~/domains/product/types/ProductDataDTO";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { resolveUrl } from "~/utils/url/urlResolver";
import { getPrimaryImage } from "~/utils/product/getPrimaryImage";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { useSrpProductDescriptors } from "~/features/search/hooks/useSrpProductDescriptors";

type JsonLdBreadcrumb = {
  "@type": "ListItem";
  position: number;
  item: {
    "@id": string;
    name: string;
  };
};

export const PLPJsonLDComponent = () => {
  const navigationBreadcrumbs = useSelector(selectNavigationBreadcrumbs);
  const totalNumberOfResults = useSelector(selectProductResultAmount);
  const { selection } = usePriceReferenceSelection();

  const productCodes = useSelector((state: RootState) =>
    state.srp.results.results.slice(0, 20).map((row) => row.productCode),
  );
  const productDescriptorList = useSrpProductDescriptors(productCodes, getInitialPriceReference(selection));

  const getBreadcrumbList = () => {
    const base = [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@id": window.location.origin + resolveUrl(HOMEPAGE),
          name: "Home",
        },
      },
    ];

    const dynamicBreadcrumbs = navigationBreadcrumbs?.reduce((acc, crumb, idx) => {
      acc.push({
        "@type": "ListItem",
        position: idx + 2,
        item: {
          "@id": window.location.origin + resolveUrl(crumb.url),
          name: crumb.name,
        },
      });
      return acc;
    }, [] as JsonLdBreadcrumb[]);

    return [...base, ...(dynamicBreadcrumbs || [])];
  };

  const getProductList = () => {
    return productDescriptorList
      .map((item) => {
        const product = item?.base as PdpPageProductData | undefined;
        if (!product) {
          return null;
        }

        const brand = product.manufacturerBrandId ?? "";
        const image = getPrimaryImage(product, { imageFormat: ImageFormat.product });
        const pdpUrl = getProductDetailsUrl(product);

        return {
          "@type": "Product",
          image: image?.url,
          url: pdpUrl,
          name: product.designation,
          brand: {
            "@type": "Brand",
            name: brand,
          },
          manufacturer: {
            "@type": "Organization",
            name: "Schaeffler",
          },
        };
      })
      .filter(Boolean);
  };

  const currentPageUrl = () => {
    const lastBreadcrumb = navigationBreadcrumbs?.[navigationBreadcrumbs.length - 1];
    const rawUrl = lastBreadcrumb?.url || "/";
    return new URL(rawUrl.startsWith("/") ? window.location.origin + rawUrl : rawUrl).toString();
  };

  const schemaData = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: getBreadcrumbList(),
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      url: currentPageUrl(),
      numberOfItems: totalNumberOfResults,
      itemListElement: getProductList(),
    },
  ];

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />;
};

const getProductDetailsUrl = (productData: PdpPageProductData) => {
  const url = resolveUrl(productData.url);
  if (!url) {
    return url;
  }
  const urlObj = new URL(url.startsWith("/") ? window.location.origin + url : url);
  return urlObj.toString();
};
